import type { ColumnDef } from "@tanstack/react-table";
import IconButton from "@/components/IconButton";
import { buildActionColumn, buildAvatarDetailsColumn } from "@/components/Table";
import { renderEmptyCellState } from "@/components/Table/utils";
import { ProgramStatusTag } from "@/features/person/components/PersonObjectHeading";
import { actionId } from "@/hooks/useTableRowMenu";
import { toFormat } from "@/lib/dateHelpers";
import { formatMemberStatus, hasNestedAchievementFields } from "./_utils";
import type { AchievementForMembersSortField } from "./queries";
import type { AchievementReportRow } from "./types";
import type { TableRowMenu } from "@/hooks/useTableRowMenu";

export const ACHIEVEMENT_REPORT_SORT_FIELD_MAP: Record<string, AchievementForMembersSortField> = {
  name: "NAME",
  memberClass: "MEMBER_CLASS",
  section: "SECTION_NAME",
  status: "STATUS",
  title: "TITLE",
  recognizedBy: "RECOGNIZING_ENTITY",
  recognizedAt: "RECOGNIZED_AT"
};

interface ColumnsOptions {
  isCompactLayout: boolean;
  onViewPerson: (pgaId: string) => void;
  setMenu: (menu: TableRowMenu) => void;
}

export function getColumns({
  isCompactLayout,
  onViewPerson,
  setMenu
}: ColumnsOptions): ColumnDef<AchievementReportRow, unknown>[] {
  const expandColumn: ColumnDef<AchievementReportRow, unknown> = {
    id: "expand",
    header: "",
    size: 40,
    minSize: 40,
    maxSize: 40,
    enableSorting: false,
    meta: {
      headerClassName: "hide-header-in-column",
      className: "pr-0"
    },
    cell: ({ row }) => {
      if (!hasNestedAchievementFields(row.original)) {
        return null;
      }

      const isExpanded = row.getIsExpanded();
      const expandLabel = isExpanded ? "Collapse achievement details" : "Expand achievement details";

      return (
        <IconButton
          name={isExpanded ? "expand_less" : "expand_more"}
          title={expandLabel}
          ariaLabel={expandLabel}
          skipMargin
          data-testid={`expand-row-${row.original.id}`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            row.getToggleExpandedHandler()();
          }}
        />
      );
    }
  };

  return [
    ...buildAvatarDetailsColumn<AchievementReportRow>({
      isCompactLayout,
      getTitle: (row) => row.name,
      getSubtitle: () => "",
      header: "Name",
      size: 240,
      id: "name",
      sortType: "text"
    }),
    {
      id: "memberClass",
      header: "Current Class",
      accessorKey: "memberClass",
      meta: { sortType: "text" },
      cell: ({ getValue }) => renderEmptyCellState(getValue())
    },
    {
      id: "section",
      header: "Current Section",
      accessorKey: "sectionName",
      meta: { sortType: "text" },
      cell: ({ getValue }) => renderEmptyCellState(getValue())
    },
    {
      id: "status",
      header: "Member Status",
      accessorKey: "status",
      meta: {
        headerClassName: "hide-header-in-column",
        skipEmptyState: true,
        sortType: "text"
      },
      cell: ({ row }) => {
        const status = formatMemberStatus(row.original.status);
        if (!status) {
          return null;
        }

        return <ProgramStatusTag status={status} />;
      }
    },
    {
      id: "title",
      header: "Achievement Title",
      accessorKey: "title",
      size: 220,
      meta: { sortType: "text" },
      cell: ({ getValue }) => renderEmptyCellState(getValue())
    },
    {
      id: "recognizedBy",
      header: "Recognized By",
      accessorKey: "recognizingEntity",
      meta: { sortType: "text" },
      cell: ({ getValue }) => renderEmptyCellState(getValue())
    },
    {
      id: "recognizedAt",
      header: "Recognized On",
      accessorKey: "recognizedAt",
      meta: { sortType: "date" },
      cell: ({ row }) => renderEmptyCellState(row.original.recognizedAt ? toFormat(row.original.recognizedAt) : null)
    },
    ...(isCompactLayout ? [] : [expandColumn]),
    buildActionColumn<AchievementReportRow>({
      actionId,
      setMenu,
      onViewClick: (row) => onViewPerson(row.pgaId),
      viewButtonTitle: "View member info"
    })
  ];
}
